import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { HiExclamationTriangle } from 'react-icons/hi2';
import { floorplannerStore } from '../../store/floorplannerStore';

type FloorplanCollabBannerProps = {
  onTakeOver: () => void;
};

/**
 * Overlay shown when another user holds the edit lease, or access was revoked.
 * Simple take-over: view-only until you take over; then edit like normal.
 */
export const FloorplanCollabBanner: React.FC<FloorplanCollabBannerProps> = observer(
  ({ onTakeOver }) => {
    const { t } = useTranslation();
    const [confirmTakeOver, setConfirmTakeOver] = useState(false);

    if (floorplannerStore.collabAccessLost) {
      return (
        <div className="pointer-events-none absolute inset-x-0 top-20 z-50 flex justify-center px-4">
          <div className="pointer-events-auto flex max-w-xl items-center gap-3 rounded-lg border border-red-300 bg-red-50 px-4 py-2 shadow-md">
            <div className="text-sm text-red-950">
              {t('collab.noAccess')}
            </div>
          </div>
        </div>
      );
    }

    // Lease hand-off could not load the latest saved version, so the canvas is
    // held view-only: editing here would save a stale plan over the other editor.
    if (floorplannerStore.collabReloadFailed) {
      return (
        <div className="pointer-events-none absolute inset-x-0 top-20 z-50 flex justify-center px-4">
          <div className="pointer-events-auto flex max-w-xl flex-wrap items-center gap-3 rounded-lg border border-red-300 bg-red-50 px-4 py-2 shadow-md">
            <div className="text-sm text-red-950">
              {t('collab.couldNotLoad')}
            </div>
            <button
              type="button"
              onClick={onTakeOver}
              className="rounded-md bg-black px-3 py-1.5 text-xs font-medium text-white hover:opacity-90"
            >
              {t('common.retry')}
            </button>
          </div>
        </div>
      );
    }

    if (!floorplannerStore.lockedByOther) return null;

    const name = floorplannerStore.editingByName || 'another user';

    return (
      <div className="pointer-events-none absolute inset-x-0 top-20 z-50 flex justify-center px-4">
        <div className="pointer-events-auto flex max-w-xl flex-col gap-2 rounded-lg border border-yellow-400 bg-yellow-200 px-4 py-2 shadow-md">
          <div className="flex items-start gap-3">
            <HiExclamationTriangle
              className="mt-0.5 h-5 w-5 flex-shrink-0 text-yellow-800"
              aria-hidden="true"
            />
            <div className="min-w-0 flex-1">
              <div className="text-sm text-yellow-950">
                {t('collab.currentlyEditedBy')}<span className="font-semibold">{name}</span>
              </div>
              <div className="mt-0.5 text-xs text-yellow-900">
                {t('collab.editedByYou', { name })}
              </div>
              <div className="mt-0.5 text-xs text-yellow-900">
                {t('collab.liveEditsHint')}
              </div>
            </div>
            {!confirmTakeOver && (
              <button
                type="button"
                onClick={() => setConfirmTakeOver(true)}
                className="flex-shrink-0 rounded-md bg-black px-3 py-1.5 text-xs font-medium text-white hover:opacity-90"
              >
                {t('collab.takeOverEditing')}
              </button>
            )}
          </div>
          {confirmTakeOver && (
            <div className="flex flex-wrap items-center gap-2">
              <div className="text-xs text-yellow-900">
                {t('collab.reloadHint')}
              </div>
              <button
                type="button"
                onClick={() => {
                  setConfirmTakeOver(false);
                  onTakeOver();
                }}
                className="rounded-md bg-black px-3 py-1.5 text-xs font-medium text-white hover:opacity-90"
              >
                {t('collab.takeOver')}
              </button>
              <button
                type="button"
                onClick={() => setConfirmTakeOver(false)}
                className="rounded-md border border-yellow-400 bg-white px-3 py-1.5 text-xs font-medium text-yellow-950 hover:bg-yellow-100"
              >
                {t('common.cancel')}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  },
);

export default FloorplanCollabBanner;
